import IdTokenVerifier from 'idtoken-verifier';
import { differenceInMilliseconds } from 'date-fns';
import {
  getLoginEmail,
  IAddress,
  IICAMToken,
  IPerson,
  IUser,
  ResponseCSOSHeaders,
  substring,
} from '..';
import { raEnabled, diEnabled, helpDeskEnabled, rpsEnabled, operationsEnabled } from '.';
import { compact, join, compose as lcompose, gte, lt } from 'lodash/fp';
import {
  path,
  pathOr,
  identity,
  always,
  add,
  equals,
  toLower,
  endsWith,
  isEmpty,
  isNil,
  anyPass,
  not,
} from 'rambda';
import { getMonoidAll } from 'fp-ts/lib/Predicate';
import { filter } from 'fp-ts/lib/Array';
import { left, right, map as emap, fold } from 'fp-ts/lib/Either';
import { flow } from 'fp-ts/lib/function';

export const mapICAMTokenToUser = (icamToken: IICAMToken) => (a: IUser) => ({
  ...a,

  ...icamToken.data.profile,
  ...path<IPerson>('data.profile.person')(icamToken),
  loginEmail: getLoginEmail(icamToken),
  icam: icamToken,
  idToken: pathOr('', 'data.id_token')(icamToken),
  refreshTokenReminderDelay: getRefreshTokenDelayMilliseconds(icamToken),
  accessTokenExpirationDelay: getTokenExpirationMilliseconds(icamToken),

  idExpirationDt: pathOr(0, 'data.profile.idExpirationDt')(icamToken),

  groups: getTokenGroups(icamToken),
  sub: getTokenSub(icamToken),
});

export const mapUserAuthenticated =
  (isAuthenticated: boolean) => (a: IUser) => ({
    ...a,
    isAuthenticated,
  });

export const normalizePhone = (a: string | null) => (a || '').substring(2);

export const isPhoneUpdateComplete = lcompose(
  equals(10),
  pathOr(0, 'length'),
  normalizePhone,
  pathOr('', 'phone')
);

export const isPhoneReadComplete = lcompose(
  equals(10),
  pathOr(0, 'length'),
  pathOr('', 'phone')
);

export function isMailToComplete(address: IAddress | undefined) {
  return (
    !!address &&
    !!address.stateCd &&
    !!address.city &&
    !!address.street1 &&
    !!address.postalCd
  );
}

export function isUserProfileReadComplete(profile: {
  phone: string | null;
  address?: IAddress;
}) {
  return isPhoneReadComplete(profile) && isMailToComplete(profile.address);
}

export function isUserProfileUpdateComplete(profile: {
  phone: string | null;
  address?: IAddress;
}) {
  return isPhoneUpdateComplete(profile) && isMailToComplete(profile.address);
}

export const viewUserProfile = (a: IUser) => (edit: boolean) =>
  !edit && isUserProfileReadComplete(a);

export const mailTo = lcompose(
  fold<null, string, string>(always('invalid address or no data'), identity),
  emap<IAddress, string>(a => {
    const items = [
      a.street1,
      a.street2,
      a.street3,
      a.city,
      a.stateCd,
      a.postalCd,
    ];
    return lcompose(join(', '), compact)(items);
  }),
  address => (address === null ? left(address) : right(address))
);

export const getToken = lcompose(
  a => (a === void 0 ? left(void 0) : right(a)),
  pathOr(void 0, 'icam.data.access_token')
);

export const getCsrfToken = lcompose(
  a => (a === void 0 ? left(void 0) : right(a)),
  pathOr(void 0, ['icam', 'headers', 'x-csrf-csos-token'])
);

export const getHeaders = lcompose(
  (a: ResponseCSOSHeaders | undefined) =>
    a === void 0 ? left(void 0) : right(a),
  pathOr(void 0, 'icam.headers')
);

export const getHeadersUnwrapped = lcompose(pathOr({}, 'icam.headers'));

export const getTokenExpirationMilliseconds = lcompose(
  // always(60 * 5 * 1000),
  // always((3600 / 2) * 1000),
  // always(180 * 1000), //TODO revert 3 minutes

  (a: number) => differenceInMilliseconds(a * 1000, Date.now()),
  pathOr(void 0, 'payload.exp'),

  b => {
    const verifier = new IdTokenVerifier({
      issuer: 'https://my.auth0.com/',
      audience: 'gYSNlU4YC4V1YPdqq8zPQcup6rJw1Mbt',
    });

    return verifier.decode(b);
  },
  (a: string) => substring(7, 0)(a),
  pathOr('', 'headers.authorization')
);

export const getTokenGroups = lcompose(
  pathOr([], 'payload.groups'),

  b => {
    const verifier = new IdTokenVerifier({
      issuer: 'https://my.auth0.com/',
      audience: 'gYSNlU4YC4V1YPdqq8zPQcup6rJw1Mbt',
    });

    return verifier.decode(b);
  },

  (a: string) => substring(7, 0)(a),
  pathOr('', 'headers.authorization')
);

export const getTokenSub = lcompose(
  pathOr('', 'payload.sub'),

  b => {
    const verifier = new IdTokenVerifier({
      issuer: 'https://my.auth0.com/',
      audience: 'gYSNlU4YC4V1YPdqq8zPQcup6rJw1Mbt',
    });

    return verifier.decode(b);
  },

  (a: string) => substring(7, 0)(a),
  pathOr('', 'headers.authorization')
);

export const refreshTokenBlackoutWindowSeconds = 60;

export const getRefreshTokenDelayMilliseconds = lcompose(
  add(-1000 * refreshTokenBlackoutWindowSeconds),
  getTokenExpirationMilliseconds
);

export const idExpired = (user: IUser) => (now: number) =>
  lcompose(
    idExpirationDt => idExpirationDt !== 0 && gte(now)(idExpirationDt),
    pathOr(0, 'idExpirationDt')
  )(user);

export const id30DaysExpired = (user: IUser) => (now: number) =>
  lcompose(
    (idExpirationDt: number) =>
      idExpirationDt !== 0 &&
      gte(now)(idExpirationDt - 2.592e9) &&
      lt(now)(idExpirationDt),
    pathOr(0, 'idExpirationDt')
  )(user);

export const deaOrDoj = flow(
  pathOr('', 'sub'),
  toLower,
  a => endsWith('@dea.gov')(a) || endsWith('@usdoj.gov')(a)
);

export const fullName = (user: IUser) =>
  flow(
    filter(flow(anyPass([isEmpty, isNil]), not)),
    join(', ')
  )([user.lastName, user.middleInitial, user.firstName]);

export const allowAdmin = getMonoidAll<IUser>().concat(
  flow(pathOr([], 'groups'), raEnabled),
  flow(deaOrDoj)
);

export const allowDi = getMonoidAll<IUser>().concat(
  flow(pathOr([], 'groups'), diEnabled),
  flow(deaOrDoj)
);

export const allowHelpDesk = flow(pathOr([], 'groups'), helpDeskEnabled);

export const allowRps = flow(pathOr([], 'groups'), rpsEnabled);

export const allowOperations = flow(pathOr([], 'groups'), operationsEnabled);
